import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createPilotSeminar,
  deleteCommunityReferral,
  deletePilotSeminar,
  getAdminReferrals,
  getAdminReferralStats,
  getCommunityReferrals,
  getPilotSeminars,
  ReferralListFilters,
  replayPartnerEvent,
  updateCommunityReferral,
  updatePartnerOnboardingState,
} from '../api/referrals';
import { useAuthState } from '../contexts/authcontext';
import {
  PilotSeminarSchema,
  ReferralOnboardingState,
  ReferralSchema,
} from '../data/types';

const referralStaleTime = 60 * 1000;

export const referralQueryKeys = {
  community: (communityId: string) => ['communityReferrals', communityId] as const,
  communityList: (communityId: string, filters?: ReferralListFilters) => [
    ...referralQueryKeys.community(communityId),
    filters || {},
  ] as const,
  admin: (filters?: ReferralListFilters & { community_id?: string }) => [
    'adminReferrals',
    filters || {},
  ] as const,
  stats: (filters?: { community_id?: string; resource_id?: string; partner_slug?: string; since?: string }) => [
    'adminReferralStats',
    filters || {},
  ] as const,
  seminars: (filters?: { community_id?: string; resource_id?: string; partner_slug?: string }) => [
    'pilotSeminars',
    filters || {},
  ] as const,
};

/** Load tenant-scoped referrals through the shared React Query cache. */
export const useCommunityReferrals = (
  filters?: ReferralListFilters,
  enabled = true,
) => {
  const { communityId, authUser } = useAuthState();
  const resolvedCommunityId = communityId || authUser?.primary_community || '';
  const query = useQuery({
    queryKey: referralQueryKeys.communityList(resolvedCommunityId, filters),
    queryFn: () => getCommunityReferrals(resolvedCommunityId, filters),
    enabled: Boolean(resolvedCommunityId && enabled),
    staleTime: referralStaleTime,
  });

  return {
    referrals: query.data || [],
    isLoadingReferrals: query.isLoading,
    isErrorReferrals: query.isError,
    reloadReferrals: query.refetch,
    communityId: resolvedCommunityId,
  };
};

/** Mutations for referral state, public onboarding state, and delivery replay. */
export const useReferralActions = () => {
  const { communityId, authUser } = useAuthState();
  const resolvedCommunityId = communityId || authUser?.primary_community || '';
  const queryClient = useQueryClient();
  const invalidateReferrals = () => {
    queryClient.invalidateQueries({
      queryKey: referralQueryKeys.community(resolvedCommunityId),
    });
    queryClient.invalidateQueries({ queryKey: ['resource_bookings'] });
    queryClient.invalidateQueries({ queryKey: ['bookings'] });
  };

  const updateMutation = useMutation({
    mutationFn: ({
      referralId,
      updates,
    }: {
      referralId: string;
      updates: Partial<ReferralSchema>;
    }) => updateCommunityReferral(resolvedCommunityId, referralId, updates),
    onSuccess: invalidateReferrals,
  });

  const onboardingMutation = useMutation({
    mutationFn: ({
      referralId,
      onboardingState,
    }: {
      referralId: string;
      onboardingState: ReferralOnboardingState;
    }) => updatePartnerOnboardingState(
      resolvedCommunityId,
      referralId,
      onboardingState,
    ),
    onSuccess: invalidateReferrals,
  });

  const replayMutation = useMutation({
    mutationFn: ({
      slug,
      eventId,
    }: {
      slug: string;
      eventId: string;
    }) => replayPartnerEvent(resolvedCommunityId, slug, eventId),
    onSuccess: invalidateReferrals,
  });

  const deleteMutation = useMutation({
    mutationFn: ({ referralId }: { referralId: string }) => (
      deleteCommunityReferral(resolvedCommunityId, referralId)
    ),
    onSuccess: invalidateReferrals,
  });

  return {
    updateReferral: updateMutation.mutateAsync,
    updateOnboardingState: onboardingMutation.mutateAsync,
    replayEvent: replayMutation.mutateAsync,
    deleteReferral: deleteMutation.mutateAsync,
    isUpdatingReferral: updateMutation.isPending || onboardingMutation.isPending,
    isReplayingEvent: replayMutation.isPending,
    isDeletingReferral: deleteMutation.isPending,
  };
};

/** Load HomeCook ops referrals for pilot exports and reporting. */
export const useAdminReferrals = (
  filters?: ReferralListFilters & { community_id?: string },
) => {
  const query = useQuery({
    queryKey: referralQueryKeys.admin(filters),
    queryFn: () => getAdminReferrals(filters),
    staleTime: referralStaleTime,
  });
  return {
    referrals: query.data || [],
    isLoadingReferrals: query.isLoading,
    reloadReferrals: query.refetch,
  };
};

/** Load aggregate pilot metrics. */
export const useAdminReferralStats = (
  filters?: { community_id?: string; resource_id?: string; partner_slug?: string; since?: string },
) => {
  const query = useQuery({
    queryKey: referralQueryKeys.stats(filters),
    queryFn: () => getAdminReferralStats(filters),
    staleTime: referralStaleTime,
  });
  return {
    stats: query.data,
    isLoadingStats: query.isLoading,
    reloadStats: query.refetch,
  };
};

/** Load and create joint-seminar registry entries. */
export const usePilotSeminars = (
  filters?: { community_id?: string; resource_id?: string; partner_slug?: string },
) => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: referralQueryKeys.seminars(filters),
    queryFn: () => getPilotSeminars(filters),
    staleTime: referralStaleTime,
  });
  const createMutation = useMutation({
    mutationFn: (body: Partial<PilotSeminarSchema>) => createPilotSeminar(body),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['pilotSeminars'] }),
        queryClient.invalidateQueries({ queryKey: ['adminReferralStats'] }),
      ]);
    },
  });
  const deleteMutation = useMutation({
    mutationFn: (seminarId: string) => deletePilotSeminar(seminarId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['pilotSeminars'] }),
        queryClient.invalidateQueries({ queryKey: ['adminReferralStats'] }),
      ]);
    },
  });

  return {
    seminars: query.data || [],
    isLoadingSeminars: query.isLoading,
    createSeminar: createMutation.mutateAsync,
    isCreatingSeminar: createMutation.isPending,
    deleteSeminar: deleteMutation.mutateAsync,
    isDeletingSeminar: deleteMutation.isPending,
  };
};
